import type { IDialogFullScreenNotificationProps, IDIALOGVERIFYTRANSACTIONPROPS } from 'BreetComponents';
import {
	CURRENCYCONFIG,
	FIXEDCACHEKEYS,
	getSafeObjectField,
	showToast,
	truncateWithEllipsis,
	type TwoFaType,
} from 'BreetHelpers';
import { useModalApi } from 'BreetHooks';
import {
	useConvertFiatToUSDMutation,
	useDisableAutoSettlementMutation,
	useRequestPaymentMutation,
	useSaveWalletAddressMutation,
	useSellUSDMutation,
	useSwapCurrencyMutation,
	useUpdateAutoSettlementMutation,
	useWithdrawUSDMutation,
} from 'BreetRedux';
import { useCallback, useMemo, useRef } from 'react';

export const useMfaTransactionsApi = () => {
	const { getModalProps, closeModalDirect, handleModalCloseAll, openModalDirect } = useModalApi();

	const [requestPayment, { isLoading: requestPaymentLoading }] = useRequestPaymentMutation();
	const [withdrawUSD, { isLoading: withdrawUSDLoading }] = useWithdrawUSDMutation();
	const [convertFiatToUSD, { isLoading: convertFiatToUSDLoading }] = useConvertFiatToUSDMutation();
	const [swapCurrency, { isLoading: swapCurrencyLoading }] = useSwapCurrencyMutation();
	const [saveWalletAddress, { isLoading: saveWalletAddressLoading, reset }] = useSaveWalletAddressMutation({
		fixedCacheKey: FIXEDCACHEKEYS.SAVE_WALLET_ADDRESS,
	});
	const [updateAutoSettlement, { isLoading: updateAutoSettlementLoading }] = useUpdateAutoSettlementMutation();
	const [disableAutoSettlement, { isLoading: disableAutoSettlementLoading }] = useDisableAutoSettlementMutation();
	const [sellUSD, { isLoading: sellUSDLoading }] = useSellUSDMutation();

	// Guards against a double money-submit: a fast double-click, or the PIN auto-submit racing with the
	// Continue handler, can dispatch the same withdraw/convert mutation twice before RTK's `isLoading`
	// flips (it only updates on the next render). A genuine retry after failure is still allowed because
	// the lock releases when the in-flight request settles.
	const inFlightRef = useRef(false);

	const modalProps = getModalProps('verifyTransaction');

	const {
		transactionType,
		skipPin,
		withdrawFiatReqData,
		withdrawUSDReqData,
		convertFiatToUSDReqData,
		reqDataInfo,
		swapCurrencyReqData,
		saveWalletAddressReqData,
		updateAutoSettlementReqData,
		disableAutoSettlementReqData,
		sellUsdReqData,
	} = (modalProps?.metadata ?? {}) as IDIALOGVERIFYTRANSACTIONPROPS;

	const fetchOTP = useCallback(
		(pinArg: string, otpArg?: TwoFaType) => {
			const withOtp = <T extends object>(body: T): T & { otp?: TwoFaType } => (otpArg ? { ...body, otp: otpArg } : body);

			const requestBuilders: Partial<Record<typeof transactionType, () => Promise<unknown>>> = {
				'withdraw-fiat': withdrawFiatReqData && (() => requestPayment(withOtp({ ...withdrawFiatReqData, pin: pinArg })).unwrap()),
				'withdrawUSD': withdrawUSDReqData && (() => withdrawUSD(withOtp({ ...withdrawUSDReqData, pin: pinArg })).unwrap()),
				'convertFiatToUSD': convertFiatToUSDReqData && (() => convertFiatToUSD(withOtp({ ...convertFiatToUSDReqData })).unwrap()),
				'swapCurrency': swapCurrencyReqData && (() => swapCurrency(withOtp({ ...swapCurrencyReqData, pin: pinArg })).unwrap()),
				'saveWalletAddress': saveWalletAddressReqData && (() => saveWalletAddress(withOtp(saveWalletAddressReqData)).unwrap()),
				'updateAutoSettlement':
					updateAutoSettlementReqData && (() => updateAutoSettlement(withOtp(updateAutoSettlementReqData)).unwrap()),
				'disableAutoSettlement':
					disableAutoSettlementReqData && (() => disableAutoSettlement(withOtp(disableAutoSettlementReqData)).unwrap()),
				'sell-usd': sellUsdReqData && (() => sellUSD(withOtp(sellUsdReqData)).unwrap()),
			};

			const builder = requestBuilders[transactionType];

			if (!builder) {
				showToast('error', 'Something went wrong');
				return;
			}

			if (inFlightRef.current) return;
			inFlightRef.current = true;

			return builder().finally(() => {
				inFlightRef.current = false;
			});
		},
		[
			withdrawFiatReqData,
			withdrawUSDReqData,
			convertFiatToUSDReqData,
			swapCurrencyReqData,
			saveWalletAddressReqData,
			updateAutoSettlementReqData,
			disableAutoSettlementReqData,
			sellUsdReqData,
			transactionType,
			requestPayment,
			withdrawUSD,
			convertFiatToUSD,
			swapCurrency,
			saveWalletAddress,
			updateAutoSettlement,
			disableAutoSettlement,
			sellUSD,
		]
	);

	const COPYICON = `<svg
        width='18'
        height='18'
        viewBox='0 0 14 14'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        class='svg-dark'
      >
        <path
          d='M6.47508 13.2702H4.02508C1.74425 13.2702 0.729248 12.2552 0.729248 9.97435V7.52435C0.729248 5.24352 1.74425 4.22852 4.02508 4.22852H6.47508C8.75592 4.22852 9.77092 5.24352 9.77092 7.52435V9.97435C9.77092 12.2552 8.75592 13.2702 6.47508 13.2702ZM4.02508 5.10352C2.21675 5.10352 1.60425 5.71602 1.60425 7.52435V9.97435C1.60425 11.7827 2.21675 12.3952 4.02508 12.3952H6.47508C8.28342 12.3952 8.89592 11.7827 8.89592 9.97435V7.52435C8.89592 5.71602 8.28342 5.10352 6.47508 5.10352H4.02508V5.10352Z'
          fill='#ffffff'
        />
        <path
          d='M9.97508 9.77018H9.33342C9.09425 9.77018 8.89592 9.57185 8.89592 9.33268V7.52435C8.89592 5.71602 8.28342 5.10352 6.47508 5.10352H4.66675C4.42758 5.10352 4.22925 4.90518 4.22925 4.66602V4.02435C4.22925 1.74352 5.24425 0.728516 7.52508 0.728516H9.97508C12.2559 0.728516 13.2709 1.74352 13.2709 4.02435V6.47435C13.2709 8.75518 12.2559 9.77018 9.97508 9.77018ZM9.77092 8.89518H9.97508C11.7834 8.89518 12.3959 8.28268 12.3959 6.47435V4.02435C12.3959 2.21602 11.7834 1.60352 9.97508 1.60352H7.52508C5.71675 1.60352 5.10425 2.21602 5.10425 4.02435V4.22852H6.47508C8.75592 4.22852 9.77092 5.24352 9.77092 7.52435V8.89518Z'
          fill='#ffffff'
        />
      </svg>`;

	const escapeAttr = (str: string) =>
		str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

	const transactionNotification = useMemo((): IDialogFullScreenNotificationProps | null => {
		if (!reqDataInfo) return null;

		const fromSymbol = getSafeObjectField(CURRENCYCONFIG, reqDataInfo.from)?.symbol ?? '';

		const conversionNotification = (amount?: number): IDialogFullScreenNotificationProps => ({
			header: 'Currency Conversion Completed',
			subheader: `Conversion Request of <b>${fromSymbol}${amount ?? ''}</b> to <br/> <b>${reqDataInfo.to}</b> completed successfully.`,
			cta: 'Close',
			ctaType: 'close',
		});

		switch (transactionType) {
			case 'convertFiatToUSD':
				return conversionNotification(convertFiatToUSDReqData?.amount);
			case 'swapCurrency':
				return conversionNotification(swapCurrencyReqData?.amount);
			case 'sell-usd':
				return conversionNotification(sellUsdReqData?.amount);
			case 'withdraw-fiat':
				return {
					header: 'Withdrawal Request Submitted',
					subheader: `Your funds will be sent to <b>${reqDataInfo.beneficiary.bankName ?? ''}</b> - <b>${reqDataInfo.beneficiary.accountNumber ?? ''}</b>.`,
					cta: 'Close',
					ctaType: 'close',
				};
			case 'withdrawUSD': {
				const rawAddress = reqDataInfo.receiverAddress ?? '';
				const truncatedAddress = truncateWithEllipsis(rawAddress, 6, 15);
				const escapedTruncatedAddress = escapeAttr(truncatedAddress);
				const escapedRawAddress = escapeAttr(rawAddress);

				return {
					header: 'Crypto Withdrawal Submitted',
					subheader: `Withdrawal Request of <b>${CURRENCYCONFIG.USD.symbol}${withdrawUSDReqData?.amount} ${reqDataInfo.token} (${reqDataInfo.network})</b> to <b>${escapedTruncatedAddress}</b><button 
          type="button" 
          class="copy-btn" 
          data-copy-value="${escapedRawAddress}" 
          style="background: none; border: none; padding: 4px; cursor: pointer; display: inline-flex; align-items: center; color: inherit;"
          title="Copy to clipboard"
        >
          ${COPYICON}
        </button> <br/>submitted successfully.`,
					cta: 'Close',
					ctaType: 'close',
				};
			}
			default:
				return null;
		}
	}, [
		reqDataInfo,
		transactionType,
		convertFiatToUSDReqData?.amount,
		swapCurrencyReqData?.amount,
		sellUsdReqData?.amount,
		withdrawUSDReqData?.amount,
		COPYICON,
	]);

	const dismissModal = useCallback(() => {
		closeModalDirect('loadingDialog');

		if (transactionType === 'saveWalletAddress') {
			reset();
			closeModalDirect('verifyTransaction');
			return;
		}

		handleModalCloseAll();
	}, [closeModalDirect, handleModalCloseAll, reset, transactionType]);

	const closeModal = useCallback(() => {
		dismissModal();

		if (transactionNotification) {
			openModalDirect({
				id: 'fullScreenNotification',
				metadata: transactionNotification,
				wrapperClassName: 'fullScreenNotificationWrapper',
				hideCloseButton: true,
			});
		}
	}, [dismissModal, openModalDirect, transactionNotification]);

	return {
		fetchOTP,
		transactionType,
		skipPin,
		closeModal,
		dismissModal,
		isApiloading:
			requestPaymentLoading ||
			withdrawUSDLoading ||
			convertFiatToUSDLoading ||
			swapCurrencyLoading ||
			saveWalletAddressLoading ||
			updateAutoSettlementLoading ||
			disableAutoSettlementLoading ||
			sellUSDLoading,
	};
};
